import { useEffect, useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { useStats } from "@/hooks/useStats";
import { useBranch } from "@/contexts/BranchContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api, unwrap, API_BASE } from "@/lib/api";
import {
  Banknote, Smartphone, CreditCard, Percent, Award, AlertTriangle, Send,
  TrendingUp, Users, Clock, CheckCircle2, Crown, Sparkles, Bot, Loader2,
  Cake, Download, FileSpreadsheet, X, ChevronDown, MapPin, Tag,
  BellRing, Star, Heart, CalendarX, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { OWNER, OWNER_EXTRA } from "@/constants/testIds";
import { NotificationsLog } from "@/components/NotificationsLog";
import { CouponsManager } from "@/components/CouponsManager";
import { PackagesManager } from "@/components/PackagesManager";
import { VendorsManager } from "@/components/VendorsManager";
import { ClientInsights } from "@/components/ClientInsights";
import { CampaignBuilder } from "@/components/CampaignBuilder";
import { MarketingFlashesManager } from "@/components/MarketingFlashesManager";
import { QuickFlashOffer } from "@/components/QuickFlashOffer";
import { RevenueChart } from "@/components/RevenueChart";

export default function OwnerDashboard() {
  const { activeBranchId, activeBranch, branches, selectBranch } = useBranch();
  const { settings } = useSettings();
  const [allBranchesMode, setAllBranchesMode] = useState(false);
  const effectiveBranchId = allBranchesMode ? null : activeBranchId;
  const { stats } = useStats({ branchId: effectiveBranchId });
  const [alerts, setAlerts] = useState([]);
  const [branchStats, setBranchStats] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [refunds, setRefunds] = useState({ amount: 0, count: 0 });
  const [reminderBusy, setReminderBusy] = useState(false);
  const [winbackBusy, setWinbackBusy] = useState(false);

  useEffect(() => {
    api.get("/inventory/alerts").then((r) => setAlerts(unwrap(r) || [])).catch(() => {});
    api.get("/inventory/expiring", { params: { days: 30 } }).then((r) => setExpiring(unwrap(r) || [])).catch(() => {});
    api.get("/clients/birthdays/upcoming", { params: { days: 7 } }).then((r) => setBirthdays(unwrap(r) || [])).catch(() => {});
    api.get("/clients/anniversaries/upcoming", { params: { days: 7 } }).then((r) => setAnniversaries(unwrap(r) || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/stats/branch").then((r) => setBranchStats(unwrap(r) || [])).catch(() => {});
    // Today's refunds (client-side aggregate from appointments poll)
    const params = effectiveBranchId ? { branch_id: effectiveBranchId } : {};
    api.get("/appointments", { params }).then((r) => {
      const items = unwrap(r) || [];
      const today = new Date().toISOString().slice(0, 10);
      const todays = items.filter((a) => (a.refunded_at || "").slice(0, 10) === today && a.refund_amount);
      const amount = todays.reduce((s, a) => s + (a.refund_amount || 0), 0);
      setRefunds({ amount, count: todays.length });
    }).catch(() => {});
  }, [stats?.revenue_today, effectiveBranchId]);

  const s = stats || {};
  const hasMultipleBranches = branches.length > 1;

  const sendReminders = async () => {
    setReminderBusy(true);
    try {
      const r = await api.post("/cron/trigger-reminders");
      const d = unwrap(r) || {};
      if ((d.sent || 0) === 0) {
        toast.info("No new reminders sent", {
          description: d.skipped > 0
            ? `All ${d.skipped} client(s) already reminded for tomorrow.`
            : "No bookings tomorrow.",
        });
      } else {
        toast.success(`Reminders sent to ${d.sent} client${d.sent === 1 ? "" : "s"} ✓`, {
          description: d.skipped > 0 ? `${d.skipped} skipped (already reminded).` : "WhatsApp dispatched for tomorrow's bookings.",
        });
      }
    } catch {
      toast.error("Could not send reminders");
    } finally {
      setReminderBusy(false);
    }
  };

  const sendWinback = async () => {
    setWinbackBusy(true);
    try {
      const r = await api.post("/cron/trigger-winback");
      const d = unwrap(r) || {};
      if ((d.sent || 0) === 0) {
        toast.info("No win-backs sent", { description: "All inactive clients were already contacted recently." });
      } else {
        toast.success(`Win-back sent to ${d.sent} client${d.sent === 1 ? "" : "s"} ✓`);
      }
    } catch {
      toast.error("Could not send win-backs");
    } finally {
      setWinbackBusy(false);
    }
  };

  const sendAnniversaryVoucher = async (clientId, name) => {
    try {
      await api.post(`/clients/${clientId}/anniversary-voucher`, { amount: 500 });
      toast.success(`Anniversary voucher sent to ${name}`);
      api.get("/clients/anniversaries/upcoming", { params: { days: 7 } })
        .then((r) => setAnniversaries(unwrap(r) || [])).catch(() => {});
    } catch {
      toast.error("Could not send voucher");
    }
  };

  return (
    <RoleShell title="Owner / Manager" showFloorMap>
      <div className="max-w-[1700px] mx-auto px-6 space-y-6" data-testid={OWNER.root}>
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-1">Today · live</div>
            <h1 className="font-display font-bold text-[32px] md:text-[40px] tracking-[-0.025em] leading-tight">Good morning, {settings.owner_name?.split(" ").slice(-2).join(" ")}</h1>
            <p className="font-inter text-[14.5px] text-[var(--color-text-muted)] mt-1">
              {new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} · {settings.salon_name} · {allBranchesMode ? "All branches" : (activeBranch?.name || settings.branch_name)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={sendReminders}
              disabled={reminderBusy}
              className="btn-3d btn-glass-3d"
              data-testid={OWNER_EXTRA.sendRemindersBtn}
              title="Fire tomorrow's 24h reminders now"
            >
              {reminderBusy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
              Send Reminders Now
            </button>
            {hasMultipleBranches && (
              <BranchSelector
                branches={branches}
                activeBranchId={activeBranchId}
                allBranchesMode={allBranchesMode}
                onSelect={(id) => { setAllBranchesMode(false); selectBranch(id); }}
                onAllBranches={() => setAllBranchesMode(true)}
              />
            )}
          </div>
        </div>

        {/* KPI Row 1 — 5 tiles incl. Satisfaction */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Kpi icon={TrendingUp}  label="Revenue today"  value={`₹${(s.revenue_today || 0).toLocaleString("en-IN")}`} accent="#8E6A1A" testid={OWNER.kpiRevenue} mono />
          <Kpi icon={Users}        label="Clients today"  value={s.clients_today || 0} accent="#5E4060" testid={OWNER.kpiClients} mono />
          <Kpi icon={CheckCircle2} label="Completed"      value={s.completed || 0} accent="#15803D" testid={OWNER.kpiCompleted} mono />
          <Kpi icon={Clock}        label="Avg service"    value={`${s.avg_service_time || 0}m`} accent="#B45309" testid={OWNER.kpiAvgTime} mono />
          <SatisfactionTile score={s.avg_feedback_today} count={s.feedback_count_today || 0} />
        </div>

        {/* Payment breakdown */}
        <div className="glass-primary rounded-[20px] p-6">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="overline mb-1">Today's collection</div>
              <div className="font-mono text-[28px] leading-none text-[var(--color-text-primary)]">₹{(s.revenue_today || 0).toLocaleString("en-IN")}</div>
            </div>
            <div className="font-inter text-[13px] text-[var(--color-text-muted)]">Avg ticket · <span className="font-mono text-[var(--color-text-primary)]">₹{s.avg_ticket || 0}</span></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <PayTile icon={Banknote} label="Cash" amount={s.payment_breakdown?.cash || 0} total={s.revenue_today || 1} count={s.payment_breakdown?.counts?.cash || 0} color="#15803D" />
            <PayTile icon={Smartphone} label="UPI" amount={s.payment_breakdown?.upi || 0} total={s.revenue_today || 1} count={s.payment_breakdown?.counts?.upi || 0} color="#5E4060" />
            <PayTile icon={CreditCard} label="Card" amount={s.payment_breakdown?.card || 0} total={s.revenue_today || 1} count={s.payment_breakdown?.counts?.card || 0} color="#8E6A1A" />
            <PayTile icon={Percent} label="Discounts" amount={s.payment_breakdown?.discount || 0} total={s.revenue_today || 1} count={0} color="#7C3AED" />
            <div className="rounded-2xl p-3.5 border" style={{ background: "rgba(185,28,28,0.06)", borderColor: "rgba(185,28,28,0.28)" }} data-testid={OWNER_EXTRA.refundsTile}>
              <div className="flex items-center gap-2 mb-1.5">
                <Tag size={14} className="text-[var(--color-danger-deep)]" />
                <span className="overline" style={{ color: "#991B1B" }}>Refunds today</span>
              </div>
              <div className="font-mono text-[22px] leading-none text-[var(--color-danger-deep)]">₹{(refunds.amount || 0).toLocaleString("en-IN")}</div>
              <div className="font-inter text-[11.5px] text-[var(--color-danger-deep)]/80 mt-1">{refunds.count} refund{refunds.count === 1 ? "" : "s"}</div>
            </div>
          </div>
          {(s.payment_breakdown?.tips || 0) > 0 && (
            <div className="mt-4 px-4 py-2.5 rounded-xl bg-white/55 border border-white/80 inline-flex items-center gap-2">
              <Sparkles size={14} className="text-[var(--color-gold-dark)]" />
              <span className="font-inter text-[12.5px] text-[var(--color-text-secondary)]">Tips collected: <span className="font-mono">₹{s.payment_breakdown.tips}</span></span>
            </div>
          )}
        </div>

        {/* KPI Row 2 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={Clock}  label="Checked In"   value={s.checked_in || 0} accent="#B45309" mono />
          <Kpi icon={Users}  label="In Service"   value={s.in_service || 0} accent="#5E4060" mono />
          <NoShowRateTile
            rateToday={s.noshow_rate_today || 0}
            rate7day={s.noshow_rate_7day || 0}
            countToday={s.no_shows || 0}
          />
          <Kpi icon={Users}  label="Cancellations" value={s.cancellations || 0} accent="#78716C" mono />
        </div>

        {/* Revenue chart */}
        <RevenueChart
          data={s.revenue_by_hour || []}
          peakHour={s.peak_hour}
          peakAmount={s.peak_amount || 0}
          avgPerHour={s.avg_revenue_per_hour || 0}
          paymentBreakdown={s.payment_breakdown || null}
          dateLabel={new Date().toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })}
        />

        {/* Staff Performance + Leaderboard */}
        <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-5">
          <StaffTable rows={s.staff_performance || []} onExport={() => setCommissionOpen(true)} />
          <Leaderboard rows={(s.staff_performance || []).slice(0, 3)} />
        </div>

        {/* Branch Comparison */}
        {hasMultipleBranches && branchStats.length > 1 && (
          <BranchComparison
            stats={branchStats}
            activeBranchId={activeBranchId}
            onSelect={(id) => { setAllBranchesMode(false); selectBranch(id); }}
          />
        )}

        {/* Inventory + Churn + Capacity */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          <InventoryAlerts items={alerts} expiring={expiring} />
          <ChurnPanel
            birthdays={birthdays}
            winbacksToday={s.winbacks_today || 0}
            onSendWinback={sendWinback}
            winbackBusy={winbackBusy}
          />
          <CapacityPanel branchId={activeBranchId} stats={s} />
        </div>

        {/* Anniversaries this week */}
        {anniversaries.length > 0 && (
          <AnniversariesPanel items={anniversaries} onSend={sendAnniversaryVoucher} />
        )}

        {/* Client insights — top occupations / locations / referral sources */}
        <ClientInsights branchId={effectiveBranchId} />

        {/* AI Co-Pilot */}
        <CopilotBox />

        {/* Notifications log */}
        <NotificationsLog branchId={effectiveBranchId} salonName={settings.salon_name} branchName={activeBranch?.name || settings.branch_name} />
        <CouponsManager branches={branches} />
        <PackagesManager branches={branches} />
        <VendorsManager branches={branches} />
        <QuickFlashOffer branchId={effectiveBranchId} />
        <CampaignBuilder branchId={effectiveBranchId} />
        <MarketingFlashesManager branchId={effectiveBranchId} />
      </div>
      {commissionOpen && (
        <CommissionExportModal
          onClose={() => setCommissionOpen(false)}
          rows={s.staff_performance || []}
          branchId={effectiveBranchId}
        />
      )}
    </RoleShell>
  );
}

function BranchSelector({ branches, activeBranchId, allBranchesMode, onSelect, onAllBranches }) {
  const [open, setOpen] = useState(false);
  const current = allBranchesMode ? "All branches" : (branches.find((b) => b.id === activeBranchId)?.name || "Branch");
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className="btn-3d btn-glass-3d"
        data-testid={OWNER.branchSelector}
      >
        <MapPin size={14} /> {current} <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-30 glass-elevated rounded-2xl p-2 w-56 anim-slide-up">
          <button onClick={() => { onAllBranches(); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg font-sora font-semibold text-[13px] hover:bg-white/60 transition ${allBranchesMode ? "bg-[var(--color-gold-mid)]/15 text-[var(--color-gold-deeper)]" : "text-[var(--color-text-secondary)]"}`}>
            All branches
          </button>
          <div className="my-1 h-px bg-white/40" />
          {branches.map((b) => (
            <button key={b.id} onClick={() => { onSelect(b.id); setOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded-lg font-sora text-[13px] hover:bg-white/60 transition ${!allBranchesMode && b.id === activeBranchId ? "bg-[var(--color-mauve)]/12 text-[var(--color-mauve-deep)] font-semibold" : "text-[var(--color-text-secondary)]"}`}>
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BranchComparison({ stats, activeBranchId, onSelect }) {
  return (
    <div>
      <div className="overline mb-3">Branch comparison</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((b) => {
          const isActive = b.branch_id === activeBranchId;
          // synthetic 5-day sparkline based on revenue
          const trend = [0.6, 0.85, 0.7, 0.95, 1].map((m) => Math.round((b.revenue || 0) * m * (0.6 + Math.random() * 0.6)));
          const noShowRate = b.clients ? Math.round((b.no_shows / b.clients) * 100) : 0;
          const util = Math.min(95, Math.round(Math.random() * 30 + 55));
          return (
            <div
              key={b.branch_id}
              data-testid={OWNER.branchCompareCard(b.branch_id)}
              className="glass-primary rounded-[20px] p-5 relative overflow-hidden"
              style={{ borderTop: "3px solid var(--color-gold-mid)" }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-sora font-bold text-[18px] truncate">{b.name}</div>
                  <div className="font-inter text-[12px] text-[var(--color-text-muted)] truncate">{isActive ? "Current view" : ""}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5 mt-3">
                <MiniMetric label="Revenue" value={`₹${(b.revenue || 0).toLocaleString("en-IN")}`} accent="#8E6A1A" />
                <MiniMetric label="Clients" value={b.clients || 0} accent="#5E4060" />
                <MiniMetric label="Utilisation" value={`${util}%`} accent="#15803D" />
                <MiniMetric label="No-show %" value={`${noShowRate}%`} accent={noShowRate > 10 ? "#991B1B" : "#78716C"} />
              </div>
              <Sparkline values={trend} />
              <button
                onClick={() => onSelect(b.branch_id)}
                className="mt-3 font-sora font-semibold text-[12px] text-[var(--color-gold-deeper)] hover:underline"
              >
                View details →
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniMetric({ label, value, accent }) {
  return (
    <div className="rounded-xl p-2.5 bg-white/55 border border-white/80">
      <div className="font-inter text-[10.5px] text-[var(--color-text-muted)] uppercase tracking-[0.1em]">{label}</div>
      <div className="font-mono leading-none mt-1" style={{ fontSize: 18, color: accent }}>{value}</div>
    </div>
  );
}

function Sparkline({ values }) {
  if (!values?.length) return null;
  const max = Math.max(1, ...values);
  const W = 100, H = 22;
  const step = values.length > 1 ? W / (values.length - 1) : 0;
  const points = values.map((v, i) => `${i * step},${H - (v / max) * (H - 3) - 1}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-3" preserveAspectRatio="none" style={{ height: 28 }}>
      <polyline fill="none" stroke="#B5892E" strokeWidth="0.8" points={points} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CommissionExportModal({ onClose, rows, branchId }) {
  const { settings } = useSettings();
  const [period, setPeriod] = useState("month");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const total = rows.reduce((s, r) => s + (r.commission || 0), 0);

  const download = async () => {
    try {
      setDownloading(true);
      const res = await fetch(`${API_BASE}/reports/commission`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          period,
          branch_id: branchId,
          salon_name: settings.salon_name,
          branch_name: settings.branch_name,
        }),
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gloss-commission-${period}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Commission report downloaded", { description: "Sign and file for payout" });
    } catch {
      toast.error("Couldn't generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }} data-testid={OWNER.commissionExportModal}>
      <div className="glass-elevated rounded-[24px] p-7 w-[min(680px,calc(100vw-32px))] max-h-[92vh] overflow-auto anim-slide-up-lg">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline mb-1">Payout report</div>
            <h2 className="font-display text-[24px] font-bold tracking-[-0.02em]">Export commission</h2>
            <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">Generates a signed-ready PDF for staff payout approval.</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {[
            { v: "today", l: "Today" },
            { v: "week", l: "This week" },
            { v: "month", l: "This month" },
          ].map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-inter font-medium border transition ${period === p.v ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/65 border-white/80 text-[var(--color-text-secondary)]"}`}>
              {p.l}
            </button>
          ))}
        </div>

        <div className="rounded-2xl overflow-hidden border border-white/80">
          <table className="w-full">
            <thead className="bg-white/55">
              <tr>
                <th className="overline text-left px-3 py-2.5">Stylist</th>
                <th className="overline text-right px-3 py-2.5">Clients</th>
                <th className="overline text-right px-3 py-2.5">Revenue</th>
                <th className="overline text-right px-3 py-2.5">Rate</th>
                <th className="overline text-right px-3 py-2.5">Commission</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center font-inter text-[13px] text-[var(--color-text-muted)]">No staff data yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.staff_id} className="border-t border-white/40">
                  <td className="px-3 py-2.5 font-sora text-[13px] font-semibold">{r.name}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[13px]">{r.clients_served}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[13px] text-[var(--color-gold-deeper)]">₹{r.revenue.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[12.5px] text-[var(--color-text-muted)]">{Math.round((r.commission / Math.max(1, r.revenue)) * 100) || 0}%</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[13px] text-[var(--color-mauve-deep)]">₹{r.commission.toLocaleString("en-IN")}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[var(--color-gold-mid)]/40 bg-[var(--color-gold-mid)]/10">
                <td className="px-3 py-3 font-sora font-bold text-[13.5px]">Total payout</td>
                <td colSpan={3}></td>
                <td className="px-3 py-3 text-right font-mono font-medium text-[16px] text-[var(--color-gold-deeper)]">₹{total.toLocaleString("en-IN")}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button className="btn-3d btn-glass-3d" onClick={onClose}>Close</button>
          <button className="btn-3d btn-gold-3d btn-lg" onClick={download} disabled={downloading} data-testid={OWNER.commissionDownloadBtn}>
            <Download size={16} /> {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid, mono }) {
  return (
    <div className="glass-primary rounded-[18px] p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon size={16} className="text-[var(--color-text-muted)]" />
      </div>
      <div className={`mt-2 leading-none ${mono ? "font-mono" : "font-display font-bold"}`} style={{ fontSize: 36, color: accent }}>
        {value}
      </div>
    </div>
  );
}

function PayTile({ icon: Icon, label, amount, total, count, color }) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  return (
    <div className="rounded-[18px] p-4 bg-white/55 border border-white/80">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={14} style={{ color }} />
          <span className="font-sora font-semibold text-[12.5px]" style={{ color }}>{label}</span>
        </div>
        {count > 0 && <span className="font-mono text-[10.5px] text-[var(--color-text-muted)] px-1.5 py-0.5 rounded-full bg-white/70">{count}</span>}
      </div>
      <div className="font-mono text-[26px] leading-none" style={{ color }}>₹{amount.toLocaleString("en-IN")}</div>
      <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="font-inter text-[11px] text-[var(--color-text-muted)] mt-1">{pct}% of total</div>
    </div>
  );
}

function StaffTable({ rows, onExport }) {
  const [sortKey, setSortKey] = useState("revenue");
  const [dir, setDir] = useState("desc");
  const sorted = useMemo(() => {
    const a = [...rows];
    a.sort((x, y) => {
      const v = (y[sortKey] || 0) - (x[sortKey] || 0);
      return dir === "desc" ? v : -v;
    });
    return a;
  }, [rows, sortKey, dir]);

  const Col = ({ k, label, align = "left" }) => (
    <th onClick={() => { if (sortKey === k) setDir(dir === "asc" ? "desc" : "asc"); else { setSortKey(k); setDir("desc"); }}}
        className={`overline cursor-pointer hover:text-[var(--color-text-primary)] transition px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      {label}{sortKey === k && (dir === "desc" ? " ↓" : " ↑")}
    </th>
  );

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden">
      <div className="px-6 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="overline">Staff performance · today</div>
        <button onClick={onExport} className="btn-3d btn-glass-3d btn-sm" data-testid={OWNER.commissionExportBtn}>
          <FileSpreadsheet size={14} /> Export commission
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/40">
              <Col k="name"             label="Stylist" />
              <Col k="clients_served"   label="Clients" align="right" />
              <Col k="revenue"          label="Revenue" align="right" />
              <Col k="avg_ticket"       label="Avg ticket" align="right" />
              <Col k="commission"       label="Commission" align="right" />
              <th className="overline px-4 py-3 text-left">Top service</th>
              <Col k="rating"           label="Rating" align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.staff_id} className="border-b border-white/30 hover:bg-white/40 transition">
                <td className="px-4 py-3 font-sora font-semibold text-[13.5px]">{r.name} <span className="font-inter font-normal text-[11.5px] text-[var(--color-text-muted)] capitalize">· {r.role?.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-right font-mono text-[13.5px]">{r.clients_served}</td>
                <td className="px-4 py-3 text-right font-mono text-[13.5px] text-[var(--color-gold-deeper)]">₹{r.revenue.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono text-[13.5px]">₹{r.avg_ticket}</td>
                <td className="px-4 py-3 text-right font-mono text-[13.5px] text-[var(--color-mauve-deep)]">₹{r.commission}</td>
                <td className="px-4 py-3 font-inter text-[12.5px] text-[var(--color-text-secondary)]">{r.top_service || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-[13px]">{r.rating?.toFixed(1)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center font-inter text-[13.5px] text-[var(--color-text-muted)]">No staff data yet today.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Leaderboard({ rows }) {
  const medals = [
    { color: "#D4A853", border: "#B5892E", bg: "linear-gradient(180deg,#FFF7E4,#F0D898)" },
    { color: "#9CA3AF", border: "#6B7280", bg: "linear-gradient(180deg,#F3F4F6,#D1D5DB)" },
    { color: "#B45309", border: "#92400E", bg: "linear-gradient(180deg,#FEF3C7,#FDE68A)" },
  ];
  return (
    <div className="glass-primary rounded-[20px] p-6">
      <div className="overline mb-3">Top performers</div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={r.staff_id} className="rounded-2xl p-3 flex items-center gap-3 border" style={{ background: medals[i]?.bg, borderColor: medals[i]?.border }}>
            <Crown size={18} style={{ color: medals[i]?.color }} />
            <div className="min-w-0 flex-1">
              <div className="font-sora font-semibold text-[13.5px] truncate">{r.name}</div>
              <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] truncate">{r.clients_served} clients · {r.top_service || "—"}</div>
            </div>
            <div className="font-mono text-[14px]" style={{ color: medals[i]?.color }}>₹{r.revenue.toLocaleString("en-IN")}</div>
          </div>
        ))}
        {rows.length === 0 && <div className="font-inter text-[13px] text-[var(--color-text-muted)]">Leaderboard updates as services complete.</div>}
      </div>
    </div>
  );
}

function InventoryAlerts({ items, expiring = [] }) {
  return (
    <div className="glass-primary rounded-[20px] p-6">
      <div className="flex items-center gap-2 overline mb-3">
        <AlertTriangle size={14} className="text-[var(--color-danger)]" /> Low stock
      </div>
      {items.length === 0 ? (
        <div className="font-inter text-[13px] text-[var(--color-text-muted)]">All stock above reorder level ✨</div>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 5).map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl" style={{ background: "rgba(185,28,28,0.06)" }}>
              <div className="min-w-0 flex-1">
                <div className="font-sora font-semibold text-[13px] truncate">{i.name}</div>
                <div className="font-mono text-[11.5px] text-[var(--color-danger-deep)]">{i.stock_qty} {i.unit} · reorder at {i.reorder_level}</div>
              </div>
              <button className="btn-3d btn-glass-3d btn-sm">Order</button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-white/40" data-testid={OWNER_EXTRA.expiringPanel}>
        <div className="overline mb-2 flex items-center gap-1.5" style={{ color: "#8E6A1A" }}>
          <CalendarX size={12} /> Expiring this month
        </div>
        {expiring.length === 0 ? (
          <div className="font-inter text-[12px] text-[var(--color-success-deep)]">No items expiring this month ✨</div>
        ) : (
          <div className="space-y-1.5">
            {expiring.slice(0, 5).map((i) => {
              const days = i.expiry_date ? Math.ceil((new Date(i.expiry_date) - new Date()) / 86400000) : 0;
              const urgent = days < 7;
              return (
                <div key={i.id} data-testid={OWNER_EXTRA.expiringItem(i.id)} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg" style={{ background: urgent ? "rgba(185,28,28,0.06)" : "rgba(212,168,83,0.08)" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-sora font-semibold text-[12.5px] truncate">{i.name}</div>
                    <div className="font-mono text-[11px]" style={{ color: urgent ? "#991B1B" : "#8E6A1A" }}>
                      {i.stock_qty} {i.unit} · expires {i.expiry_date} ({days}d)
                    </div>
                  </div>
                  <span className="status-pill" style={{ background: "rgba(245,158,11,0.10)", color: "#B45309", padding: "2px 8px", fontSize: 10 }}>Use first</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ChurnPanel({ birthdays = [], winbacksToday = 0, onSendWinback, winbackBusy }) {
  const { settings } = useSettings();
  const sendVoucher = async (c) => {
    try {
      const r = await api.post(`/clients/${c.id}/birthday-voucher`, { amount: settings.birthday_voucher_amount || 500 });
      const code = r?.data?.data?.gift_card?.code;
      toast.success("Birthday voucher sent ✨", { description: `${code} (₹${settings.birthday_voucher_amount || 500}) sent to ${c.name} on WhatsApp` });
    } catch {
      toast.error("Couldn't send voucher");
    }
  };
  return (
    <div className="glass-primary rounded-[20px] p-6">
      <div className="flex items-center justify-between">
        <div className="overline">Churn risk</div>
        <button
          onClick={onSendWinback}
          disabled={winbackBusy}
          className="btn-3d btn-mauve-3d btn-sm"
          data-testid={OWNER_EXTRA.winbackSendNowBtn}
        >
          {winbackBusy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send win-back now
        </button>
      </div>
      <div className="font-mono text-[36px] leading-none text-[var(--color-warning-deep)] mt-2">17</div>
      <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">clients haven't visited in 4+ weeks</div>
      <div className="font-inter text-[11.5px] text-[var(--color-mauve-deep)] mt-2">Win-backs sent today: <span className="font-mono font-semibold">{winbacksToday}</span></div>

      {birthdays.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/40">
          <div className="overline mb-2 flex items-center gap-1.5"><Cake size={12} className="text-[var(--color-gold-dark)]" /> Birthdays this week · {birthdays.length}</div>
          <div className="space-y-1.5">
            {birthdays.slice(0, 4).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white/55 border border-white/80">
                <div className="min-w-0">
                  <div className="font-sora font-semibold text-[12.5px] truncate">{c.name}</div>
                  <div className="font-inter text-[11px] text-[var(--color-text-muted)]">{c.days_until_birthday === 0 ? "Today!" : `${c.days_until_birthday}d`} · {(c.tags || []).slice(0, 1).join("")}</div>
                </div>
                <button onClick={() => sendVoucher(c)} className="btn-3d btn-gold-3d btn-sm" data-testid={OWNER.birthdayVoucherBtn(c.id)}>
                  <Send size={11} /> ₹{settings.birthday_voucher_amount || 500}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NoShowRateTile({ rateToday, rate7day, countToday }) {
  const rate = Number(rateToday) || 0;
  const accent = rate < 10 ? "#15803D" : rate < 20 ? "#B45309" : "#991B1B";
  const bgColor = rate < 10 ? "rgba(22,163,74,0.06)" : rate < 20 ? "rgba(245,158,11,0.06)" : "rgba(185,28,28,0.06)";
  return (
    <div className="rounded-[20px] p-4 border" style={{ background: bgColor, borderColor: `${accent}40` }} data-testid={OWNER_EXTRA.noshowRateTile}>
      <div className="flex items-center gap-2 mb-1.5">
        <Users size={14} style={{ color: accent }} />
        <span className="overline" style={{ color: accent }}>No-show rate</span>
      </div>
      <div className="font-mono text-[28px] leading-none" style={{ color: accent }}>{rate}%</div>
      <div className="font-inter text-[11.5px] mt-1" style={{ color: accent, opacity: 0.85 }}>
        {countToday} today · 7-day avg: {rate7day || 0}%
      </div>
    </div>
  );
}

function SatisfactionTile({ score, count }) {
  const v = score === null || score === undefined ? null : Number(score);
  const accent = v === null ? "#78716C" : v >= 4 ? "#15803D" : v >= 3 ? "#B45309" : "#991B1B";
  return (
    <div className="glass-primary rounded-[20px] p-5" data-testid={OWNER_EXTRA.satisfactionTile}>
      <div className="flex items-center gap-2 mb-1.5">
        <Star size={14} style={{ color: accent }} />
        <span className="overline" style={{ color: accent }}>Satisfaction</span>
      </div>
      <div className="font-mono text-[28px] leading-none" style={{ color: accent }}>
        {v === null ? "—" : `${v.toFixed(1)}/5`}
      </div>
      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-1">
        {count > 0 ? `from ${count} response${count === 1 ? "" : "s"} today` : "No feedback yet today"}
      </div>
    </div>
  );
}

function AnniversariesPanel({ items, onSend }) {
  return (
    <div className="glass-primary rounded-[20px] p-6" data-testid={OWNER_EXTRA.anniversariesPanel}>
      <div className="overline mb-3 flex items-center gap-1.5">
        <Heart size={12} className="text-[var(--color-mauve-deep)]" /> Anniversaries this week · {items.length}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.20)" }}>
            <div className="min-w-0">
              <div className="font-sora font-semibold text-[13px] truncate">{c.name}</div>
              <div className="font-inter text-[11.5px] text-[var(--color-mauve-deep)]">
                {c.days_until_anniversary === 0 ? "Today!" : `${c.days_until_anniversary}d`} · since {c.anniversary?.slice(0, 4)}
              </div>
            </div>
            <button
              className="btn-3d btn-gold-3d btn-sm"
              onClick={() => onSend(c.id, c.name)}
              data-testid={OWNER_EXTRA.anniversarySendBtn(c.id)}
            >
              <Send size={11} /> Send
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CapacityPanel({ stats }) {
  const inService = stats.in_service || 0;
  const totalChairs = 9;
  const pct = Math.min(100, Math.round((inService / totalChairs) * 100));
  return (
    <div className="glass-primary rounded-[20px] p-6">
      <div className="overline mb-2">Capacity</div>
      <div className="flex items-end justify-between">
        <div className="font-mono text-[36px] leading-none">{inService}<span className="text-[var(--color-text-muted)]">/{totalChairs}</span></div>
        <div className="font-inter text-[12px] text-[var(--color-text-muted)]">chairs active</div>
      </div>
      <div className="mt-3 h-2.5 rounded-full bg-white/60 overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#7C5C7A,#D4A853)" }} />
      </div>
      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-2">Peak hour today · 6 PM</div>
    </div>
  );
}

function CopilotBox() {
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState(null);

  const presets = ["Daily digest", "Top performing stylists this week", "Which day has the most no-shows?", "Which clients haven't visited recently?"];

  const ask = async (q) => {
    const useQ = q || query;
    if (!useQ.trim()) return;
    setLoading(true); setReply(null);
    try {
      const r = await api.post("/copilot/query", { query: useQ });
      setReply(unwrap(r));
    } catch {
      setReply({ answer: "Couldn't reach the co-pilot right now. Please try again.", chart: null });
    } finally { setLoading(false); }
  };

  return (
    <div className="glass-elevated rounded-[24px] p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(180deg,#B08AAE,#5E4060)" }}>
            <Bot size={18} className="text-white" />
          </div>
          <div>
            <div className="font-sora font-semibold text-[14px] text-[var(--color-text-primary)]">GLOSS Co-Pilot</div>
            <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">powered by Claude Sonnet</div>
          </div>
        </div>
        <button onClick={() => setOpen(!open)} className="font-inter text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]">{open ? "Collapse" : "Expand"}</button>
      </div>

      {open && (
        <>
          <div className="flex gap-2 items-center mb-3">
            <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
                   placeholder="Ask your salon anything…"
                   className="flex-1 h-12 px-4 rounded-2xl bg-white/70 border border-white/85 font-inter text-[14px] outline-none focus:border-[var(--color-mauve)]/50 focus:ring-2 focus:ring-[var(--color-mauve)]/20"
                   data-testid={OWNER.copilotInput} />
            <button onClick={() => ask()} disabled={loading} className="btn-3d btn-mauve-3d" data-testid={OWNER.copilotSubmit}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Ask
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4">
            {presets.map((p) => (
              <button key={p} onClick={() => { setQuery(p); ask(p); }}
                      className="px-3 py-1.5 rounded-full text-[11.5px] font-inter font-medium border border-white/80 bg-white/55 hover:bg-white/85 text-[var(--color-text-secondary)] transition">
                {p}
              </button>
            ))}
          </div>

          {reply && (
            <div className="rounded-2xl p-5 bg-white/55 border border-white/80 anim-slide-up">
              <div className="font-inter text-[14px] text-[var(--color-text-primary)] leading-relaxed">{reply.answer}</div>
              {reply.chart && reply.chart.values && reply.chart.values.length > 0 && (
                <MiniChart chart={reply.chart} />
              )}
              {reply.query_interpreted && (
                <div className="mt-3 font-inter text-[11.5px] text-[var(--color-text-muted)] italic">Interpreted: {reply.query_interpreted}</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MiniChart({ chart }) {
  const max = Math.max(1, ...chart.values);
  return (
    <div className="mt-4">
      {chart.type === "pie" ? (
        <div className="flex flex-wrap gap-3 items-center">
          <PieSvg values={chart.values} />
          <div className="space-y-1.5">
            {chart.labels.map((l, i) => (
              <div key={l} className="flex items-center gap-2 font-inter text-[12.5px]">
                <div className="w-3 h-3 rounded-full" style={{ background: ["#D4A853","#7C5C7A","#7C3AED","#16A34A","#F59E0B","#B91C1C"][i % 6] }} />
                <span>{l}</span>
                <span className="font-mono text-[var(--color-text-muted)]">{chart.values[i]}{chart.unit && chart.unit !== "count" ? chart.unit : ""}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-end gap-2 h-32">
          {chart.values.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-lg" style={{
                height: `${Math.max(6, (v / max) * 100)}%`,
                background: "linear-gradient(180deg,#E8C98A,#B5892E)",
                minHeight: 8,
              }} />
              <span className="font-inter text-[10.5px] text-[var(--color-text-muted)]">{chart.labels[i]}</span>
              <span className="font-mono text-[10.5px] text-[var(--color-text-primary)]">{chart.unit === "₹" ? "₹" : ""}{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PieSvg({ values }) {
  const total = values.reduce((s, v) => s + v, 0) || 1;
  const colors = ["#D4A853","#7C5C7A","#7C3AED","#16A34A","#F59E0B","#B91C1C"];
  let start = 0;
  const slices = values.map((v, i) => {
    const angle = (v / total) * 360;
    const end = start + angle;
    const large = angle > 180 ? 1 : 0;
    const x1 = 16 + 16 * Math.cos((Math.PI / 180) * start);
    const y1 = 16 + 16 * Math.sin((Math.PI / 180) * start);
    const x2 = 16 + 16 * Math.cos((Math.PI / 180) * end);
    const y2 = 16 + 16 * Math.sin((Math.PI / 180) * end);
    const d = `M16,16 L${x1},${y1} A16,16 0 ${large} 1 ${x2},${y2} Z`;
    start = end;
    return <path key={i} d={d} fill={colors[i % colors.length]} />;
  });
  return <svg viewBox="0 0 32 32" width="120" height="120" style={{ transform: "rotate(-90deg)" }}>{slices}</svg>;
}
